import React from 'react';
import { Box, Typography, Grid, Paper, Stack, Divider } from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import HomeIcon from '@mui/icons-material/Home';
import AccessibilityIcon from '@mui/icons-material/Accessibility';

const RiskFactorsTriad = () => {
  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
        The Three Major Categories of Fall Risk Factors
      </Typography>
      
      <Grid container spacing={3}>
        {/* Physical Risk Factors */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'warning.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessibilityIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Physical Factors
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Muscle Weakness
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Particularly in lower extremities, reduces ability to maintain balance
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Gait & Balance Problems
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Unsteady walking, difficulty with transfers, poor coordination
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Sensory Deficits
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Vision impairment, reduced proprioception, vestibular disorders
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Chronic Conditions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Arthritis, Parkinson's, stroke, diabetes, orthostatic hypotension
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Functional Limitations
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Difficulty with ADLs, mobility challenges, use of assistive devices
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Environmental Risk Factors */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HomeIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Environmental Factors
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                  Floor Hazards
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Loose rugs, cluttered walkways, uneven surfaces, slippery floors
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                  Poor Lighting
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Dim lighting, high glare, poor contrast between surfaces
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                  Bathroom Risks
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Lack of grab bars, slippery surfaces, low toilet seats
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                  Furniture Issues
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Unstable furniture, inappropriate heights, lack of armrests
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                  Stairs & Transitions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Lack of handrails, uneven steps, unmarked thresholds
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Medication Risk Factors */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'error.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MedicationIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Medication Factors
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                  Psychotropics
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Antidepressants, antipsychotics, sedatives, anxiolytics
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                  Cardiovascular Medications
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Antihypertensives, diuretics, vasodilators, beta-blockers
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                  Polypharmacy
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Taking 4+ medications significantly increases fall risk
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                  Medication Side Effects
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Dizziness, orthostatic hypotension, sedation, blurred vision
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                  Medication Interactions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Combined effects of multiple medications can amplify fall risk
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'primary.main' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
          Key Insight: The Interaction Effect
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Fall risk increases exponentially when factors from multiple categories are present simultaneously. 
          For example, a person taking sedating medication (medication factor) who has poor balance (physical factor) 
          and lives in a poorly lit home with loose rugs (environmental factor) has a significantly higher fall risk 
          than someone with factors from only one category.
        </Typography>
      </Box>
    </Box>
  );
};

export default RiskFactorsTriad;
